// SAR / ground-team trainer core (SAR-1..4 / E-32/34, M36). PURE + deterministic
// (ENG-1): the ELT beacon store, its received-signal-strength model, a coarse
// terrain shadow against the elevation heightmap, an MGRS/lat-lon coordinate
// conversion, the direction-finder bearing math, and the "locate the beacon"
// objective state machine. The host owns placement, the HUD, and multiplayer
// scoring; every number here is reproducible on any peer (E-32 authoritative RSSI).
import { SAR } from '../config';

export interface Vec3 { x: number; y: number; z: number; }

/** An emergency-locator-transmitter beacon: an always-loaded objective entity
 *  (E-32) at a fixed world position, emitting omnidirectionally to SAR.ELT_RANGE. */
export interface Beacon {
  readonly id: number;
  x: number; y: number; z: number;
}

// ── Signal model (SAR-2) ─────────────────────────────────────────────────────

/** Horizontal (plan-view) distance in blocks from a searcher to a beacon — the
 *  signal model is a ground path loss, so altitude does not change the bars. */
export function beaconDist(pos: Vec3, b: Beacon): number {
  return Math.hypot(pos.x - b.x, pos.z - b.z);
}

/** In omnidirectional range of the beacon? Hard cutoff at SAR.ELT_RANGE (SAR-3). */
export function inRange(pos: Vec3, b: Beacon): boolean {
  return beaconDist(pos, b) <= SAR.ELT_RANGE;
}

/**
 * Received signal strength (SAR-2): a log-distance path-loss model
 * `P0 − 10·N·log10(max(1,dist)) − terrainShadow`. Strictly decreasing in distance
 * (closer = stronger) so the trainee homes by watching the bars rise; returns
 * −Infinity beyond range (no signal at all, SAR-3).
 */
export function rssi(dist: number, terrainShadow = 0): number {
  if (dist > SAR.ELT_RANGE) return -Infinity;
  return SAR.RSSI_P0 - 10 * SAR.PATHLOSS_N * Math.log10(Math.max(1, dist)) - terrainShadow;
}

/** A usable signal (drives the DF, SAR-4): in range AND above the no-signal floor
 *  (terrain shadow can push an in-range beacon below the floor → blanked). */
export function hasSignal(dist: number, terrainShadow = 0): boolean {
  return dist <= SAR.ELT_RANGE && rssi(dist, terrainShadow) >= SAR.RSSI_FLOOR;
}

/** Signal bars 0..maxBars for the HUD (0 = no signal); RSSI_FLOOR..P0 → 0..maxBars. */
export function signalBars(dist: number, terrainShadow: number, maxBars: number): number {
  const r = rssi(dist, terrainShadow);
  if (!Number.isFinite(r) || r < SAR.RSSI_FLOOR) return 0;
  const t = (r - SAR.RSSI_FLOOR) / (SAR.RSSI_P0 - SAR.RSSI_FLOOR);
  return Math.max(1, Math.min(maxBars, Math.round(t * maxBars)));
}

/**
 * Coarse line-of-sight terrain shadow (SAR-2): sample the straight line from the
 * searcher to the beacon (one sample ≈ per block, capped) and return a dB penalty
 * proportional to the greatest amount the ground rises ABOVE that line of sight
 * (a hill between you and the ELT weakens the signal), capped at SAR.SHADOW_MAX.
 * `groundY(x,z)` is the elevation heightmap (ELV); flat terrain → 0.
 */
export function terrainShadow(from: Vec3, b: Beacon, groundY: (x: number, z: number) => number): number {
  const dx = b.x - from.x, dz = b.z - from.z;
  const dist = Math.hypot(dx, dz);
  if (dist < 1) return 0;
  const steps = Math.min(Math.ceil(dist), 128);
  let maxIntrusion = 0;
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const losY = from.y + (b.y - from.y) * t; // the straight sight line
    const intrusion = groundY(from.x + dx * t, from.z + dz * t) - losY;
    if (intrusion > maxIntrusion) maxIntrusion = intrusion;
  }
  return Math.min(SAR.SHADOW_MAX, maxIntrusion * SAR.SHADOW_PER_BLOCK);
}

// ── Direction finders (SAR-4) ────────────────────────────────────────────────

/** Wrap an angle to (−π, π]. */
export function wrapPi(a: number): number {
  return Math.atan2(Math.sin(a), Math.cos(a));
}

/**
 * Relative bearing (radians, (−π,π]) from a searcher facing `facingYaw` to a
 * beacon — the DF needle/LED input (SAR-4). Yaw follows the player/aircraft frame
 * (0 = −z = north), so `doa = atan2(bx−px, −(bz−pz))` is the beacon's true bearing.
 * Returns null when there is no usable signal (out of range or terrain-shadowed);
 * at dist≈0 (standing on the ELT) the bearing is degenerate → 0 = "dead ahead"
 * (E-34: the DF reads at-source rather than NaN).
 */
export function bearingToBeacon(
  pos: Vec3, facingYaw: number, b: Beacon | null, shadow = 0,
): number | null {
  if (!b) return null;
  const dx = b.x - pos.x, dz = b.z - pos.z;
  const dist = Math.hypot(dx, dz);
  if (!hasSignal(dist, shadow)) return null;
  if (dist < 1e-6) return 0; // on the beacon (E-34)
  const doa = Math.atan2(dx, -dz); // true bearing in the yaw frame (0 = north/−z)
  return wrapPi(doa - facingYaw);
}

/**
 * Which handheld DF LED lights for a relative bearing (SAR-4). SAR.DF_LEDS LEDs
 * over ±SAR.DF_FOV_DEG: the centre LED = beacon dead ahead, left/right LEDs =
 * beacon to your left/right, saturating at the end LEDs beyond the FOV. Returns
 * null (all dark) when there is no signal.
 */
export function ledIndex(relBearing: number | null): number | null {
  if (relBearing === null) return null;
  const mid = (SAR.DF_LEDS - 1) / 2;
  const fov = (SAR.DF_FOV_DEG * Math.PI) / 180;
  const idx = Math.round(mid + (relBearing / fov) * mid);
  return Math.max(0, Math.min(SAR.DF_LEDS - 1, idx));
}

// ── MGRS / lat-lon coordinate readout (SAR-1) ────────────────────────────────
//    WGS84 → UTM (transverse-Mercator series, Snyder) → MGRS grid reference.
//    The geodetic constants below are the standard WGS84 ellipsoid + the fixed
//    MGRS lettering scheme — contract numbers like the NOAA terms in solar.ts.

const WGS84_A = 6_378_137.0;            // semi-major axis (m)
const WGS84_F = 1 / 298.257223563;      // flattening
const UTM_K0 = 0.9996;                  // UTM scale factor
const E2 = WGS84_F * (2 - WGS84_F);     // eccentricity²
const EP2 = E2 / (1 - E2);              // second eccentricity²
const DEG = Math.PI / 180;

/** UTM latitude band letter (C..X, no I/O), or 'Z' outside ±80° (E-29 guard). */
function utmBand(lat: number): string {
  if (lat < -80 || lat > 84) return 'Z';
  const bands = 'CDEFGHJKLMNPQRSTUVWX';
  return bands[Math.max(0, Math.min(bands.length - 1, Math.floor((lat + 80) / 8)))]!;
}

export interface Utm { zone: number; band: string; easting: number; northing: number; }

/** WGS84 lat/lon → UTM easting/northing (Snyder transverse-Mercator forward). */
export function latLonToUtm(lat: number, lon: number): Utm {
  const zone = Math.floor((lon + 180) / 6) + 1;
  const lon0 = ((zone - 1) * 6 - 180 + 3) * DEG; // central meridian
  const phi = lat * DEG, lam = lon * DEG;
  const sinP = Math.sin(phi), cosP = Math.cos(phi), tanP = Math.tan(phi);
  const N = WGS84_A / Math.sqrt(1 - E2 * sinP * sinP);
  const T = tanP * tanP;
  const C = EP2 * cosP * cosP;
  const A = (lam - lon0) * cosP;
  const M = WGS84_A * (
    (1 - E2 / 4 - 3 * E2 * E2 / 64 - 5 * E2 * E2 * E2 / 256) * phi
    - (3 * E2 / 8 + 3 * E2 * E2 / 32 + 45 * E2 * E2 * E2 / 1024) * Math.sin(2 * phi)
    + (15 * E2 * E2 / 256 + 45 * E2 * E2 * E2 / 1024) * Math.sin(4 * phi)
    - (35 * E2 * E2 * E2 / 3072) * Math.sin(6 * phi)
  );
  const easting = UTM_K0 * N * (A + (1 - T + C) * A ** 3 / 6
    + (5 - 18 * T + T * T + 72 * C - 58 * EP2) * A ** 5 / 120) + 500_000;
  let northing = UTM_K0 * (M + N * tanP * (A * A / 2
    + (5 - T + 9 * C + 4 * C * C) * A ** 4 / 24
    + (61 - 58 * T + T * T + 600 * C - 330 * EP2) * A ** 6 / 720));
  if (lat < 0) northing += 10_000_000; // false northing, southern hemisphere
  return { zone, band: utmBand(lat), easting, northing };
}

const E100 = ['ABCDEFGH', 'JKLMNPQR', 'STUVWXYZ'];             // 100km column sets by (zone-1)%3
const N100 = ['ABCDEFGHJKLMNPQRSTUV', 'FGHJKLMNPQRSTUVABCDE']; // 100km row sets by zone parity

/** WGS84 lat/lon → an MGRS grid reference at `precision` digits (1..5 = 10km..1m).
 *  Formatted with single spaces, e.g. `18S UJ 2282 0699` (SAR-1 coordinate readout). */
export function latLonToMgrs(lat: number, lon: number, precision = 5): string {
  const u = latLonToUtm(lat, lon);
  const col = E100[(u.zone - 1) % 3]![Math.floor(u.easting / 100_000) - 1] ?? '?';
  const rowSeq = N100[(u.zone - 1) % 2]!;
  const row = rowSeq[Math.floor(u.northing / 100_000) % 20]!;
  const p = Math.max(1, Math.min(5, precision));
  const div = 10 ** (5 - p);
  const e = Math.floor((u.easting % 100_000) / div).toString().padStart(p, '0');
  const n = Math.floor((u.northing % 100_000) / div).toString().padStart(p, '0');
  return `${u.zone}${u.band} ${col}${row} ${e} ${n}`;
}

// ── ELT beacon store (an always-loaded objective entity, E-32) ────────────────

/** The deployed ELT beacons. Small + pure so the RSSI/objective are reproducible;
 *  the host mirrors deploy/remove into the world and (in MP) authoritatively. */
export class BeaconField {
  private readonly list: Beacon[] = [];
  private nextId = 1;

  get beacons(): readonly Beacon[] { return this.list; }
  get count(): number { return this.list.length; }

  deploy(x: number, y: number, z: number): Beacon {
    const b: Beacon = { id: this.nextId++, x, y, z };
    this.list.push(b);
    return b;
  }
  remove(b: Beacon): void { const i = this.list.indexOf(b); if (i >= 0) this.list.splice(i, 1); }
  clear(): void { this.list.length = 0; }

  /** The nearest in-range beacon to `pos` (the DF homes on the nearest, SAR-4), or
   *  null if none is within SAR.ELT_RANGE. */
  nearestInRange(pos: Vec3): Beacon | null {
    let best: Beacon | null = null, bestD = Infinity;
    for (const b of this.list) {
      const d = beaconDist(pos, b);
      if (d <= SAR.ELT_RANGE && d < bestD) { best = b; bestD = d; }
    }
    return best;
  }
}

// ── "Locate the beacon" objective (SAR-3) ────────────────────────────────────

/** Per-searcher progress toward a find (dwell ticks within SAR.FIND_RADIUS). */
export class SarObjective {
  private readonly dwell = new Map<string, number>();
  private readonly found = new Set<string>();

  /** Advance one searcher one tick against the beacon; returns true on the exact
   *  tick they first score a find (dwell ≥ SAR.DWELL within SAR.FIND_RADIUS). */
  update(searcher: string, pos: Vec3, beacon: Beacon | null): boolean {
    if (!beacon) { this.dwell.set(searcher, 0); return false; }
    const within = beaconDist(pos, beacon) <= SAR.FIND_RADIUS;
    const d = within ? (this.dwell.get(searcher) ?? 0) + 1 : 0;
    this.dwell.set(searcher, d);
    if (within && d >= SAR.DWELL && !this.found.has(searcher)) { this.found.add(searcher); return true; }
    return false;
  }
  dwellOf(searcher: string): number { return this.dwell.get(searcher) ?? 0; }
  hasFound(searcher: string): boolean { return this.found.has(searcher); }
  /** Everyone who has scored a find (team scoring, SAR-3). */
  get finders(): readonly string[] { return [...this.found]; }
  reset(): void { this.dwell.clear(); this.found.clear(); }
}
